import {
  AutoAwesome,
  CheckCircle,
  DashboardCustomizeOutlined,
  ExpandMore,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import {
  DASHBOARD_PRESET_ICON,
  DASHBOARD_PRESET_PROFILES,
  DASHBOARD_REPORT_CATEGORIES,
  DashboardProfile,
  DashboardReportCategoryId,
  DashboardReportId,
  getReportsByCategory,
  reportsMatch,
  sortReportIdsByDialogOrder,
} from "features/dashboard/constants/dashboardReports";
import { useCallback, useEffect, useMemo, useState } from "react";

const BLANK_TEMPLATE_ID = "blank";

interface IDashboardProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (profile: DashboardProfile) => void | Promise<void>;
  activeProfile: DashboardProfile;
  customProfiles: DashboardProfile[];
  required?: boolean;
}

function DashboardProfileDialog({
  open,
  onClose,
  onApply,
  activeProfile,
  customProfiles,
  required = false,
}: IDashboardProfileDialogProps) {
  const theme = useTheme();
  const [saving, setSaving] = useState(false);
  const [templateId, setTemplateId] = useState<string>(
    required ? DASHBOARD_PRESET_PROFILES[0].id : activeProfile.id,
  );
  const [profileName, setProfileName] = useState(
    required ? "" : activeProfile.name,
  );
  const [selectedReportIds, setSelectedReportIds] = useState<
    DashboardReportId[]
  >(
    required ? DASHBOARD_PRESET_PROFILES[0].reportIds : activeProfile.reportIds,
  );

  const selectedTemplate = useMemo(() => {
    if (templateId === BLANK_TEMPLATE_ID) {
      return undefined;
    }

    return (
      DASHBOARD_PRESET_PROFILES.find((profile) => profile.id === templateId) ||
      customProfiles.find((profile) => profile.id === templateId)
    );
  }, [customProfiles, templateId]);

  const isModifiedFromTemplate = useMemo(() => {
    if (!selectedTemplate) {
      return true;
    }

    return !reportsMatch(selectedReportIds, selectedTemplate.reportIds);
  }, [selectedReportIds, selectedTemplate]);

  const requiresName = !selectedTemplate?.isPreset || isModifiedFromTemplate;
  const nameError =
    requiresName && profileName.trim().length === 0
      ? "Give your dashboard a name"
      : undefined;
  const reportsError =
    selectedReportIds.length === 0 ? "Select at least one report" : undefined;

  const resetFromProfile = useCallback(
    (profile: DashboardProfile, forceSetup = false) => {
      if (forceSetup) {
        const preset = DASHBOARD_PRESET_PROFILES[0];
        setTemplateId(preset.id);
        setProfileName("");
        setSelectedReportIds(preset.reportIds);
        return;
      }

      setTemplateId(profile.id);
      setProfileName(profile.isPreset ? "" : profile.name);
      setSelectedReportIds(profile.reportIds);
    },
    [],
  );

  useEffect(() => {
    if (open) {
      resetFromProfile(activeProfile, required);
    }
  }, [activeProfile, open, required, resetFromProfile]);

  const handleTemplateChange = (nextTemplateId: string) => {
    setTemplateId(nextTemplateId);

    if (nextTemplateId === BLANK_TEMPLATE_ID) {
      setProfileName("");
      setSelectedReportIds([]);
      return;
    }

    const template =
      DASHBOARD_PRESET_PROFILES.find(
        (profile) => profile.id === nextTemplateId,
      ) || customProfiles.find((profile) => profile.id === nextTemplateId);

    if (!template) {
      return;
    }

    setProfileName(template.isPreset ? "" : template.name);
    setSelectedReportIds(template.reportIds);
  };

  const isCategorySelected = (categoryId: DashboardReportCategoryId) => {
    const reports = getReportsByCategory(categoryId);
    return reports.some((report) => selectedReportIds.includes(report.id));
  };

  const toggleCategory = (categoryId: DashboardReportCategoryId) => {
    const reports = getReportsByCategory(categoryId);
    const reportIds = reports.map((report) => report.id);
    const selected = isCategorySelected(categoryId);

    setSelectedReportIds((current) => {
      if (selected) {
        return sortReportIdsByDialogOrder(
          current.filter((id) => !reportIds.includes(id)),
        );
      }

      return sortReportIdsByDialogOrder([...current, ...reportIds]);
    });
  };

  const toggleReport = (reportId: DashboardReportId) => {
    setSelectedReportIds((current) => {
      if (current.includes(reportId)) {
        return sortReportIdsByDialogOrder(
          current.filter((id) => id !== reportId),
        );
      }

      return sortReportIdsByDialogOrder([...current, reportId]);
    });
  };

  const handleApply = async () => {
    if (nameError || reportsError || saving) {
      return;
    }

    if (selectedTemplate?.isPreset && !isModifiedFromTemplate) {
      setSaving(true);
      try {
        await onApply(selectedTemplate);
        onClose();
      } finally {
        setSaving(false);
      }
      return;
    }

    const existingCustomProfile = customProfiles.find(
      (profile) => profile.id === templateId && !profile.isPreset,
    );

    setSaving(true);
    try {
      await onApply({
        id: existingCustomProfile?.id ?? "",
        name: profileName.trim() || selectedTemplate?.name || "My dashboard",
        reportIds: sortReportIdsByDialogOrder(selectedReportIds),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const fieldRadius = `${Number(theme.shape.borderRadius) * 1.5}px`;
  const selectedBg = alpha(theme.palette.primary.main, 0.06);
  const selectedBorder = theme.palette.primary.main;

  return (
    <CustomDialog
      open={open}
      onClose={required ? undefined : onClose}
      fullWidth
      maxWidth="md"
      allowBackdropClick={!required}
      showClose={!required}
      disableEscapeKeyDown={required}
    >
      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 4, pb: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              color="text.primary"
              sx={{ letterSpacing: -0.3 }}
            >
              Build your dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              Choose what matters most and we&apos;ll tailor your home view.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Start from
            </Typography>
            <FormControl fullWidth>
              <Select
                value={templateId}
                onChange={(event) =>
                  handleTemplateChange(event.target.value as string)
                }
                displayEmpty
                IconComponent={ExpandMore}
                renderValue={(value) => {
                  const id = value as string;
                  if (id === BLANK_TEMPLATE_ID) {
                    return (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: selectedBg,
                            color: "primary.main",
                            flexShrink: 0,
                          }}
                        >
                          <DashboardCustomizeOutlined fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Start from scratch
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Pick the reports you want to include.
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  }

                  const profile =
                    DASHBOARD_PRESET_PROFILES.find((item) => item.id === id) ||
                    customProfiles.find((item) => item.id === id);

                  return (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: selectedBg,
                          color: "primary.main",
                          flexShrink: 0,
                        }}
                      >
                        <DASHBOARD_PRESET_ICON fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          {profile?.name || "Select a preset"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                        >
                          {profile?.description ||
                            (profile?.isPreset
                              ? "Preset dashboard"
                              : "Your custom dashboard")}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                }}
                sx={{
                  borderRadius: fieldRadius,
                  "& .MuiSelect-select": {
                    py: 1.25,
                    px: 1.5,
                  },
                }}
              >
                <ListSubheader>Presets</ListSubheader>
                {DASHBOARD_PRESET_PROFILES.map((profile) => (
                  <MenuItem
                    key={profile.id}
                    value={profile.id}
                    sx={{ py: 1.25 }}
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {profile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {profile.description}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
                {customProfiles.length > 0 && (
                  <ListSubheader>Your dashboards</ListSubheader>
                )}
                {customProfiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </MenuItem>
                ))}
                <ListSubheader>Other</ListSubheader>
                <MenuItem value={BLANK_TEMPLATE_ID}>
                  Start from scratch
                </MenuItem>
              </Select>
              {selectedTemplate?.isPreset && isModifiedFromTemplate && (
                <FormHelperText>
                  You changed this preset. Name it to save as your own
                  dashboard.
                </FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Dashboard name
            </Typography>
            <TextField
              size="medium"
              fullWidth
              value={profileName}
              onChange={(event) => setProfileName(event.target.value)}
              required={requiresName}
              error={!!nameError}
              helperText={nameError}
              placeholder="e.g. Growth Overview"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <AutoAwesome sx={{ color: "primary.main", fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: fieldRadius,
                },
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              What do you want to focus on?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              mt={0.5}
              mb={1.5}
            >
              Select at least one · You can always change this later.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 1.5,
                alignItems: "start",
              }}
            >
              {DASHBOARD_REPORT_CATEGORIES.map((category) => {
                const reports = getReportsByCategory(category.id);
                const selected = isCategorySelected(category.id);
                const Icon = category.icon;
                const selectedCount = reports.filter((report) =>
                  selectedReportIds.includes(report.id),
                ).length;

                return (
                  <Box
                    key={category.id}
                    sx={{
                      border: "1px solid",
                      borderColor: selected
                        ? selectedBorder
                        : theme.palette.divider,
                      bgcolor: selected ? selectedBg : "background.paper",
                      borderRadius: fieldRadius,
                      transition: theme.transitions.create(
                        ["border-color", "background-color"],
                        { duration: theme.transitions.duration.shorter },
                      ),
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => toggleCategory(category.id)}
                      sx={{
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                        border: 0,
                        background: "transparent",
                        p: 1.5,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: selected ? "primary.main" : "text.secondary",
                          mt: 0.15,
                          flexShrink: 0,
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          color={selected ? "primary.main" : "text.primary"}
                        >
                          {category.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.35,
                            display: "block",
                            mt: 0.25,
                          }}
                        >
                          {category.description}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          color: selected ? "primary.main" : "text.disabled",
                          flexShrink: 0,
                          lineHeight: 0,
                        }}
                      >
                        {selected ? (
                          <CheckCircle fontSize="small" />
                        ) : (
                          <RadioButtonUnchecked fontSize="small" />
                        )}
                      </Box>
                    </Box>

                    <Collapse in={selected}>
                      <Box
                        sx={{
                          px: 1.5,
                          pb: 1.5,
                          pt: 0,
                          borderTop: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.12,
                          )}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1.25, mb: 0.5 }}
                        >
                          Reports ({selectedCount}/{reports.length})
                        </Typography>
                        <FormGroup>
                          {reports.map((report) => (
                            <FormControlLabel
                              key={report.id}
                              sx={{
                                alignItems: "flex-start",
                                ml: 0,
                                mr: 0,
                                "& .MuiFormControlLabel-label": {
                                  pt: 0.75,
                                },
                              }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedReportIds.includes(
                                    report.id,
                                  )}
                                  onChange={() => toggleReport(report.id)}
                                  onClick={(event) => event.stopPropagation()}
                                />
                              }
                              label={
                                <Box>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {report.label}
                                    </Typography>
                                    {!report.available && (
                                      <Typography
                                        variant="caption"
                                        color="primary.main"
                                        fontWeight={700}
                                        sx={{
                                          px: 0.75,
                                          py: 0.15,
                                          borderRadius: 1,
                                          bgcolor: alpha(
                                            theme.palette.primary.main,
                                            0.08,
                                          ),
                                        }}
                                      >
                                        Coming Soon
                                      </Typography>
                                    )}
                                  </Stack>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {report.description}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    display="block"
                                    sx={{ mt: 0.25 }}
                                  >
                                    Based on {report.sourceReport}
                                  </Typography>
                                </Box>
                              }
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
            {reportsError && (
              <FormHelperText error sx={{ mt: 1 }}>
                {reportsError}
              </FormHelperText>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: 1.75,
              borderRadius: fieldRadius,
              bgcolor: selectedBg,
            }}
          >
            <AutoAwesome
              sx={{ color: "primary.main", fontSize: 18, mt: 0.15 }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                We&apos;ll personalize your home view
              </Typography>
              <Typography variant="caption" color="text.secondary">
                You can add, remove or reorder reports anytime.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, pb: 2.5, pt: 1 }}>
        {!required && (
          <Button onClick={onClose} color="inherit" size="large" disabled={saving}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!!nameError || !!reportsError || saving}
          fullWidth={required}
          size="large"
          sx={{ borderRadius: fieldRadius, px: 3 }}
        >
          {saving ? "Saving..." : required ? "Continue" : "Apply dashboard"}
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}

export default DashboardProfileDialog;
